import React, { useState, useRef, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import GrassurnLogo from "./GrassurnLogo";
import { gsap } from "gsap";

const NAV_LINKS = [
  { label: "Services",  action: "services",  href: "/#services"  },
  { label: "Portfolio", action: "portfolio", href: "/portfolio"  },
  { label: "Craft",     action: "craft",     href: "/craft"      },
  { label: "FAQ",       action: "faq",       href: "/faq"        },
  { label: "About",     action: "about",     href: "/#about"     },
  { label: "Careers",   action: "careers",   href: "/careers"    },
];

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const barRef = useRef(null);
  const logoRef = useRef(null);
  const navLinksRef = useRef([]);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      // Nav links only. CTA button is intentionally excluded from GSAP so it
      // is never stranded at opacity:0 by StrictMode cleanup.
      const navLinks = navLinksRef.current.slice(0, NAV_LINKS.length).filter(Boolean);

      const tl = gsap.timeline();
      tl.from(barRef.current, { y: -64, opacity: 0, duration: 0.6, ease: "power3.out" })
        .from(logoRef.current, { opacity: 0, scale: 0.9, duration: 0.45, ease: "power2.out" }, "-=0.3")
        .from(navLinks, { opacity: 0, y: -8, stagger: 0.08, duration: 0.35, ease: "power2.out" }, "-=0.25");
    });

    return () => ctx.revert();
  }, []);

  // Section anchors only. Route links are plain <Link>s and need no handler.
  // Returning without preventDefault lets the browser follow the real href,
  // which is what keeps these crawlable (audit T1).
  const handleSectionClick = (e, action) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== "/") return; // let the anchor navigate home + hash
    e.preventDefault();
    document.getElementById(action)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      ref={barRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        background: "rgba(8, 14, 14, 0.55)",
        backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
        borderBottom: "1px solid rgba(91, 148, 144, 0.12)",
        height: "64px",
        display: "flex",
        alignItems: "center",
      }}
    >
      <div
        className="header-inner"
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "0 40px",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Logo */}
        <Link
          ref={logoRef}
          to="/"
          onClick={() => { if (location.pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="header-logo-btn"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            display: "flex",
            alignItems: "center",
            filter: "drop-shadow(0 0 10px rgba(91,148,144,0.4))",
          }}
          aria-label="Go to homepage"
        >
          <GrassurnLogo size="header" isDark={true} />
        </Link>

        {/* Desktop nav */}
        <nav style={{ display: "flex", alignItems: "center", gap: "32px" }} className="header-nav-wrap">
          <div className="hidden md:flex" style={{ alignItems: "center", gap: "32px" }}>
            {NAV_LINKS.map((link, i) => {
              const navStyle = {
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: "Montserrat, sans-serif",
                fontSize: "13px",
                fontWeight: 500,
                color: "rgba(255,255,255,0.55)",
                padding: 0,
                textDecoration: "none",
                transition: "color 0.2s",
              };
              const hoverProps = {
                onMouseEnter: (e) => (e.currentTarget.style.color = "#fff"),
                onMouseLeave: (e) => (e.currentTarget.style.color = "rgba(255,255,255,0.55)"),
              };
              const setRef = (el) => { navLinksRef.current[i] = el; };

              return link.href.startsWith("/#") ? (
                <a
                  key={link.label}
                  ref={setRef}
                  href={link.href}
                  onClick={(e) => handleSectionClick(e, link.action)}
                  style={navStyle}
                  {...hoverProps}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  ref={setRef}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={navStyle}
                  {...hoverProps}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Get in Touch CTA — shared between desktop and mobile breakpoints.
              header-mobile-git-btn applies the mobile size/padding overrides. */}
          <a
            href="/#contact"
            onClick={(e) => handleSectionClick(e, "contact")}
            className="header-mobile-git-btn"
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              color: "#fff",
              background: "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)",
              border: "1px solid rgba(91,148,144,0.55)",
              borderRadius: "7px",
              padding: "8px 18px",
              cursor: "pointer",
              textDecoration: "none",
              display: "inline-block",
              transition: "all 0.2s ease",
              boxShadow: "0 0 22px rgba(91,148,144,0.32), 0 4px 14px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.14)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, #479a96 0%, #3a7a77 100%)";
              e.currentTarget.style.boxShadow = "0 0 32px rgba(91,148,144,0.48), 0 6px 20px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.18)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)";
              e.currentTarget.style.boxShadow = "0 0 22px rgba(91,148,144,0.32), 0 4px 14px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.14)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Get in Touch
          </a>

          {/* Mobile hamburger */}
          <button
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: "6px" }}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>
      </div>

      {/* Mobile drawer */}
      {isMobileMenuOpen && (
        <div
          className="mobile-drawer"
          style={{
            position: "absolute",
            top: "64px",
            left: 0,
            right: 0,
            background: "#0A0A0A",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            padding: "16px 40px 24px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {NAV_LINKS.map((link) => {
            const drawerStyle = {
              background: "none",
              border: "none",
              cursor: "pointer",
              fontFamily: "Montserrat, sans-serif",
              fontSize: "14px",
              fontWeight: 500,
              color: "rgba(255,255,255,0.7)",
              textAlign: "left",
              textDecoration: "none",
              padding: 0,
            };

            return link.href.startsWith("/#") ? (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleSectionClick(e, link.action)}
                style={drawerStyle}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                style={drawerStyle}
              >
                {link.label}
              </Link>
            );
          })}
        </div>
      )}
    </header>
  );
};

export default Header;
